/**
 * Share-link attribution (master-plan item 17, CRE-846 program).
 *
 * Every copied/shared URL gets `utm_source=share&utm_medium=<surface>` so a
 * genuine share arrival is unambiguous in PostHog forever (and the kp-landing
 * split stays honest: a `?kp=` hit WITHOUT utm_source=share is an organic/SEO
 * or LLM arrival, WITH it is a human share).
 *
 * Invariants:
 * - existing query params (notably the `?kp=` keypoint deep-link) are
 *   preserved untouched;
 * - existing utm_* on the URL are never clobbered (a backend-minted sharing
 *   URL that some day carries campaign tags wins);
 * - canonicals are unaffected: canonical tags are path-derived in metadata,
 *   query params never enter them;
 * - a malformed URL is returned as-is rather than breaking the share flow.
 */
export const SHARE_UTM_SOURCE = 'share';

/** utm_medium vocabulary for share surfaces. Extend, never repurpose. */
export type ShareUtmMedium =
  | 'home_feed_card'
  | 'video_modal'
  | 'keypoint'
  | 'article_analysis';

export const withShareUtm = (url: string, medium: ShareUtmMedium): string => {
  if (!url) return url;
  try {
    // Client-side (the only place share buttons run) uses the live origin, so
    // a staging visitor shares a staging URL. The SSR fallback follows the
    // deployed env (NEXT_PUBLIC_APP_URL, provided by Next on the server)
    // rather than a hardcoded host. Read via process.env, not the validated
    // `@env`, so this client util doesn't pull the whole env schema into every
    // consumer's bundle/test. If it's ever unset, `new URL` throws on a
    // relative input and the catch below returns the URL untouched.
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : process.env.NEXT_PUBLIC_APP_URL;
    const u = new URL(url, base);
    if (!u.searchParams.has('utm_source')) {
      u.searchParams.set('utm_source', SHARE_UTM_SOURCE);
    }
    if (!u.searchParams.has('utm_medium')) {
      u.searchParams.set('utm_medium', medium);
    }
    return u.toString();
  } catch {
    return url;
  }
};
